import {
  SetStateAction,
  Dispatch,
  ClipboardEventHandler,
  KeyboardEvent,
  useState,
} from 'react';
import {
  DevHubAlert,
  DevHubAutocomplete,
  DevHubButton,
  DevHubChip,
  DevHubDynamicIcon,
  DevHubFilter,
  DevHubGrid,
  DevHubIconButton,
  DevHubInputAdornment,
  DevHubPaper,
  DevHubTextField,
  DevHubTextFieldProps,
} from '@devxdiscover/devhub-ui';
import { ApolloError } from '@apollo/client';
import { Progress } from '@backstage/core-components';
import { Device } from '../types/InsightsTypes';
import {
  DEVICE_ENVIRONMENT_LIST,
  DEVICE_TIER_LIST,
} from '../../../util/constants';

type Props = {
  loading: boolean;
  error: ApolloError | undefined;
  selectedEnvs: string[];
  handleEnvChange: (env: string) => void;
  selectedTiers: string[];
  handleTierChange: (status: string) => void;
  deviceList: Device[];
  multiline?: boolean;
  searchExecuted: boolean;
  setSearchExecuted: Dispatch<SetStateAction<boolean>>;
  handleSearch: (a: string[]) => void;
  clearDeviceList: () => void;
  hasMoreResults: boolean;
  errorOccurred: boolean;
};

/**
 * DeviceSearchGlobal searches for devices
 * @param props Props of the component
 * @param props.setApps Set applications on parent to search results
 * @param props.multiline Whether or not to allow multiline input
 * @param props.handleSearch Function to handle search
 * @param props.clearDeviceList Function to clear device list
 * @param props.deviceList List of devices
 * @param props.searchExecuted Whether or not search has been executed
 * @param props.setSearchExecuted Function to set search executed
 * @param props.selectedEnvs List of selected environments
 * @param props.handleEnvChange Function to handle environment change
 * @param props.selectedTiers List of selected tiers
 * @param props.handleTierChange Function to handle tier change
 * @param props.loading Whether or not the query is loading
 * @param props.error Error from query
 * @param props.hasMoreResults Whether or not there are more results
 * @param props.errorOccurred Whether or not an error occurred
 * @returns JSX.Element
 */
const DeviceSearchGlobal = (props: Props) => {
  const [searchTerm, setSearchTerm] = useState<string[]>([]);

  const { loading, error } = props;

  const clearSearch = () => {
    setSearchTerm([]);
    props.setSearchExecuted(false);
    props.clearDeviceList();
  };

  const handleOnChange = (value: any) => {
    if (value?.length <= 0) {
      clearSearch();
      return;
    }

    if (value[value.length - 1] === ',') {
      let values = value.split(',');
      values = values.filter((searchValue: string) => searchValue.length > 0);
      const existingValues = searchTerm;
      values.forEach((searchValue: string) => {
        if (
          !existingValues.includes(searchValue) &&
          searchValue.trim().length > 0
        ) {
          existingValues.push(searchValue.trim());
        }
      });

      clearSearch();
      setSearchTerm(existingValues);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const value = (event.target as HTMLInputElement).value;
    if (event?.key === 'Enter' || event?.key === ',') {
      event.preventDefault();
      if (value.length > 0) {
        if (!searchTerm.includes(value)) {
          clearSearch();
          setSearchTerm([...searchTerm, value]);
        } else {
          clearSearch();
          setSearchTerm([...searchTerm]);
        }
      }
    }
  };

  const handlePaste: ClipboardEventHandler<HTMLDivElement> = event => {
    event.preventDefault();
    const searchBoxText = event?.clipboardData?.getData('Text');
    const values = searchBoxText?.split(',');
    const filteredValues = values?.filter(
      (searchValue: string) => searchValue.trim().length > 0,
    );
    const existingValues = searchTerm;
    filteredValues?.forEach((searchValue: string) => {
      if (!existingValues.includes(searchValue) && searchValue.length > 0) {
        existingValues.push(searchValue);
      }
    });
    clearSearch();
    setSearchTerm(existingValues);
  };

  const handleChipDelete = (index: number) => {
    const values = searchTerm;
    values.splice(index, 1);
    if (values.length === 0) {
      clearSearch();
      return;
    }
    setSearchTerm([...values]);
  };

  const renderAlertMessage = () => {
    const totalCount = props?.deviceList?.length || 0;
    const resultsText = totalCount === 1 ? 'result' : 'results';

    return `Showing ${totalCount} ${resultsText} for "${searchTerm.toString()}"`;
  };

  const renderErrorMessage = () => (
    <DevHubGrid item>
      {loading && <Progress />}
      {props.searchExecuted &&
        error &&
        !props.hasMoreResults &&
        props.errorOccurred && (
          <div>
            <DevHubAlert variant="filled" severity="error">
              Error loading data
            </DevHubAlert>
          </div>
        )}
      {!loading &&
        props.searchExecuted &&
        (props?.deviceList?.length === 0 ? (
          <DevHubGrid item xs={12}>
            <DevHubAlert variant="outlined" severity="error">
              No results found for "{searchTerm.toString()}"
            </DevHubAlert>
          </DevHubGrid>
        ) : (
          <DevHubGrid item xs={12}>
            <DevHubAlert variant="outlined" severity="success">
              {renderAlertMessage()}
            </DevHubAlert>
          </DevHubGrid>
        ))}
    </DevHubGrid>
  );

  const renderFilters = () => (
    <>
      <DevHubGrid item>
        <DevHubFilter
          list={DEVICE_TIER_LIST}
          title="Environment"
          selectedItems={props.selectedEnvs}
          handleChange={props.handleEnvChange}
        />
      </DevHubGrid>
      <DevHubGrid item>
        <DevHubFilter
          list={DEVICE_ENVIRONMENT_LIST}
          title="Tier"
          selectedItems={props.selectedTiers}
          handleChange={props.handleTierChange}
        />
      </DevHubGrid>
    </>
  );

  const renderTags = (
    tagValue: string[],
    getTagProps: (options: { index: number }) => object,
  ) => {
    return tagValue.map((option: string, index: number) => (
      <DevHubChip
        key={index}
        onMouseDown={(event: { stopPropagation: () => any }) =>
          event.stopPropagation()
        }
        onDelete={() => handleChipDelete(index)}
        deleteIcon={
          <div
            role="button"
            tabIndex={-10}
            onMouseDown={event => {
              event.stopPropagation();
              handleChipDelete(index);
            }}
          >
            <DevHubDynamicIcon iconName="CancelOutlined" />
          </div>
        }
        noBottomMargin
        {...getTagProps({ index })}
        label={option}
      />
    ));
  };

  return (
    <>
      <DevHubPaper>
        <DevHubGrid container alignItems="center">
          <DevHubGrid item xs={12}>
            {error && props.errorOccurred ? (
              <DevHubAutocomplete
                options={[]}
                freeSolo
                multiple
                size="medium"
                value={searchTerm}
                onChange={(...params) => {
                  if (params.length >= 1) {
                    const [, value] = params;
                    handleOnChange(value);
                  }
                }}
                filterSelectedOptions
                renderTags={renderTags}
                renderInput={(params: DevHubTextFieldProps) => (
                  <DevHubTextField
                    error
                    label="Error searching for devices."
                    defaultValue="No Devices Available"
                    helperText="Please try again later"
                    variant="outlined"
                    noMargin
                    value={searchTerm}
                    onPaste={handlePaste}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                      endAdornment: (
                        <DevHubInputAdornment position="end">
                          <DevHubIconButton onClick={clearSearch}>
                            <DevHubDynamicIcon iconName="Clear" />
                          </DevHubIconButton>
                        </DevHubInputAdornment>
                      ),
                    }}
                    {...params}
                  />
                )}
              />
            ) : (
              <DevHubAutocomplete
                options={[]}
                freeSolo
                multiple
                size="medium"
                value={searchTerm}
                onChange={(...params) => {
                  if (params.length >= 1) {
                    const [, value] = params;
                    handleOnChange(value);
                  }
                }}
                filterSelectedOptions
                renderTags={renderTags}
                renderInput={(params: DevHubTextFieldProps) => (
                  <DevHubTextField
                    id="device-search"
                    label="Search By FQDN, IP, or Serial Number (Press enter or comma to add search value)"
                    margin="normal"
                    variant="outlined"
                    noMargin
                    size="small"
                    value={searchTerm}
                    onPaste={handlePaste}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                      endAdornment: (
                        <DevHubInputAdornment position="end">
                          <DevHubIconButton onClick={clearSearch}>
                            <DevHubDynamicIcon iconName="Clear" />
                          </DevHubIconButton>
                        </DevHubInputAdornment>
                      ),
                    }}
                    {...params}
                  />
                )}
              />
            )}
            {renderErrorMessage()}
          </DevHubGrid>

          <DevHubButton
            buttonColor="black"
            size="large"
            height="large"
            variant="contained"
            disabled={searchTerm.length === 0}
            onClick={() => props.handleSearch(searchTerm)}
          >
            Search
          </DevHubButton>
          {renderFilters()}
          {props.selectedEnvs.length > 0 && (
            <>
              {props.selectedEnvs.map((status: string, index: number) => (
                <DevHubGrid item key={index}>
                  <DevHubChip
                    noMargins
                    label={status}
                    onDelete={() => props.handleEnvChange(status)}
                  />
                </DevHubGrid>
              ))}
            </>
          )}
          {props.selectedTiers.length > 0 && (
            <>
              {props.selectedTiers.map((status: string, index: number) => (
                <DevHubGrid item key={index}>
                  <DevHubChip
                    noMargins
                    label={status}
                    onDelete={() => props.handleTierChange(status)}
                  />
                </DevHubGrid>
              ))}
            </>
          )}
        </DevHubGrid>
      </DevHubPaper>
    </>
  );
};

export default DeviceSearchGlobal;
